import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import TextCard from './TextCard';

const SearchContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const SearchHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
`;

const SearchTitle = styled.h1`
  color: white;
  margin-bottom: 1rem;
`;

const SearchQuery = styled.span`
  color: #FFD93D;
  font-weight: bold;
`;

const SearchStats = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-top: 1rem;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.8);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const SearchSuggestions = styled.div`
  margin-top: 2rem;
`;

const SuggestionTitle = styled.h3`
  color: white;
  margin-bottom: 1rem;
`;

const SuggestionTags = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SuggestionTag = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#667eea' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.9)'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#5a6fd8' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const SearchResults = ({
  searchQuery,
  searchResults,
  categories,
  onToggleFavorite,
  isFavorite
}) => {
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredResults = selectedCategory === 'all'
    ? searchResults
    : searchResults.filter(result => result.category === selectedCategory);

  const searchSuggestions = [
    '拍了拍', '搞笑', '暖心', '撒娇', '职场', '节日', '恋爱', '友情'
  ];

  const handleSuggestionClick = (suggestion) => {
    // 这里应该更新搜索查询，但由于我们需要传递到父组件，先简化处理
    window.location.href = `/search?q=${encodeURIComponent(suggestion)}`;
  };

  return (
    <SearchContainer className="fade-in">
      <BackButton to="/">
        ← 返回首页
      </BackButton>

      <SearchHeader>
        <SearchTitle>
          搜索结果：<SearchQuery>"{searchQuery}"</SearchQuery>
        </SearchTitle>
        <SearchStats>
          找到 {filteredResults.length} 条相关文字
        </SearchStats>
      </SearchHeader>

      {searchResults.length > 0 && (
        <CategoryFilter>
          <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold' }}>
            筛选分类：
          </span>
          <FilterButton
            active={selectedCategory === 'all'}
            onClick={() => setSelectedCategory('all')}
          >
            全部 ({searchResults.length})
          </FilterButton>
          {categories.map(category => {
            const count = searchResults.filter(r => r.category === category.id).length;
            if (count === 0) return null;

            return (
              <FilterButton
                key={category.id}
                active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon} {category.name} ({count})
              </FilterButton>
            );
          })}
        </CategoryFilter>
      )}

      <ResultsGrid className="grid-2">
        {filteredResults.map((result, index) => {
          const category = categories.find(cat => cat.id === result.category);
          return (
            <TextCard
              key={index}
              text={result.text}
              category={category}
              onToggleFavorite={() => onToggleFavorite(result.text, result.category)}
              isFavorite={isFavorite(result.text)}
            />
          );
        })}
      </ResultsGrid>

      {filteredResults.length === 0 && (
        <EmptyState>
          <EmptyIcon>🔍</EmptyIcon>
          <h3>没有找到相关结果</h3>
          <p>试试其他关键词，或者浏览下面的推荐搜索</p>

          <SearchSuggestions>
            <SuggestionTitle>推荐搜索</SuggestionTitle>
            <SuggestionTags>
              {searchSuggestions.map(suggestion => (
                <SuggestionTag
                  key={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </SuggestionTag>
              ))}
            </SuggestionTags>
          </SearchSuggestions>
        </EmptyState>
      )}
    </SearchContainer>
  );
};

export default SearchResults;
